/**
 * Enquête n°22 — Adapté de l'Étude de Cas CCS n°18
 * (Différents types de transmission moto.)
 */

import { Lightbulb, Quote, ShieldCheck, AlertTriangle, Users } from 'lucide-react';

export const transmissionMotoEnquete = {
  slug: 'transmission-moto',
  themeCode: 'V',
  issueNumber: 22,
  totalIssues: 22,
  series: 'enquetes',
  category: 'Enquête véhicule & mécanique',
  shortTitle: 'Chaîne / Cardan / Courroie',
  title: 'Chaîne, cardan, courroie : choisis la transmission qui te ressemble',
  kicker: 'Enquête n°22 · Série « Témoignages & Sécurité »',
  subtitle:
    "3 systèmes, 3 philosophies. La chaîne (sportive, légère, entretien tous les 500 km), le cardan (routier, increvable, sans entretien), la courroie (rare mais silencieuse). Comment choisir, comment entretenir, combien ça coûte.",
  heroImage: '/img/magazines/mecanique.jpg',
  heroImageAlt: 'Moto Harley Davidson Livewire électrique — illustration de la mécanique et transmission 2 roues',
  readTimeMin: 7,
  seoDescription:
    "Enquête sur les types de transmission moto : chaîne, cardan, courroie. Avantages, inconvénients, entretien, coûts, choix selon usage. Adapté de l'étude de cas CCS n°18.",
  seoKeywords:
    'transmission moto, chaîne moto, cardan moto, courroie moto, entretien chaîne, BMW, Harley, Triumph',

  edito: {
    title: 'Le système qui fait toute la différence',
    body: "On achète une moto pour son moteur, son design, sa selle… jamais pour sa transmission. Pourtant, c'est elle qui décide de ton style de vie de motard : <strong>chaîne</strong> = tu graisses tous les 500 km, tu retends, tu remplaces tous les 30 000 km. <strong>Cardan</strong> = tu changes l'huile tous les 20 000 km et tu oublies. <strong>Courroie</strong> = tu profites du silence et tu pries pour ne pas casser un kilomètre après la garantie. Choisis bien.",
    signedBy: 'La rédaction Code 2 Roues',
  },

  sections: [
    {
      type: 'lesson',
      icon: ShieldCheck,
      partLabel: 'Chapitre 1',
      title: 'La CHAÎNE — la transmission majoritaire (80 % du parc)',
      paragraphs: [
        '<strong>Principe</strong> : pignon de sortie de boîte + chaîne + couronne de roue arrière. Transmission par maillons articulés.',
        "<strong>Avantages</strong> : légère, peu coûteuse, performante, permet de changer les rapports finals (pignon/couronne) pour adapter le couple ou la vitesse de pointe.",
        "<strong>Inconvénients</strong> : <strong>entretien tous les 500 km</strong> (graissage chaîne), retension régulière (tous les 1000-2000 km), bruit, saleté (graisse projetée), remplacement du kit chaîne tous les 25 000-40 000 km (coût 150-400 €).",
        '<strong>Usage cible</strong> : sportives, roadsters, supermotards, motos polyvalentes. Toutes les Yamaha MT, Kawasaki Z, Honda CB, Triumph Speed Triple, Ducati Monster, KTM Duke.',
      ],
      keyPoints: [
        '80 % du parc moto',
        'Graisse tous les 500 km',
        'Kit à changer tous les 25-40k km',
        'Modulable (pignon/couronne)',
      ],
},
    {
      type: 'lesson',
      icon: ShieldCheck,
      partLabel: 'Chapitre 2',
      title: 'Le CARDAN — le luxe sans entretien (15 %)',
      paragraphs: [
        "<strong>Principe</strong> : arbre de transmission rigide reliant la boîte à la roue arrière via un couple conique (pignons à 90°).",
        '<strong>Avantages</strong> : <strong>aucun entretien</strong> hormis vidange d’huile tous les 20-30 000 km, durée de vie quasi infinie, propreté (pas de graisse), silence relatif.',
        '<strong>Inconvénients</strong> : lourd (+5 à 8 kg), coûteux à la construction, transmission non modulable (rapport fixe), « effet cardan » (léger levé d’arrière à l’accélération), réparation coûteuse en cas de casse (rare mais existe).',
        '<strong>Usage cible</strong> : grosses routières GT (BMW R 1250 RT, Honda GoldWing), trails routiers (BMW R 1250 GS, Triumph Tiger 1200), maxi-customs.',
      ],
      keyPoints: [
        'Zéro entretien',
        '+5 à 8 kg',
        'Vidange tous les 20-30k km',
        'Routière / GT',
      ],
},
    {
      type: 'lesson',
      icon: ShieldCheck,
      partLabel: 'Chapitre 3',
      title: 'La COURROIE — la rare et silencieuse (5 %)',
      paragraphs: [
        "<strong>Principe</strong> : courroie en kevlar/carbone, fonctionne comme une chaîne mais en matière composite.",
        '<strong>Avantages</strong> : <strong>silencieuse</strong>, sans entretien quotidien, propre (pas de graisse), légère. Compromis chaîne/cardan.',
        '<strong>Inconvénients</strong> : <strong>fragilité</strong> aux gravillons et corps étrangers, remplacement coûteux (200-400 €) et périlleux (souvent en concession), durée de vie limitée (30-50 000 km), non modulable.',
        '<strong>Usage cible</strong> : motos custom et cruisers américains (Harley-Davidson principalement, certains modèles BMW F800 ou Buell).',
      ],
      keyPoints: [
        '5 % du parc',
        'Silencieuse mais fragile',
        'Custom / cruiser',
        '30-50k km de durée',
      ],
},
    {
      type: 'didyouknow',
      icon: Lightbulb,
      title: 'L’art oublié du graissage de chaîne',
      body: "Une chaîne mal entretenue, c'est <strong>30 % de puissance perdue</strong>, <strong>1 dent de couronne sautée</strong> en virage (chute possible) et un budget multiplié par 3. La règle d’or : graisser à froid, après nettoyage du surplus de l’ancienne graisse, en faisant tourner la roue lentement. Tous les 500 km. Toujours. Si tu n’as pas envie de t’en occuper → prends une moto à cardan.",
    },
    {
      type: 'tip',
      icon: Users,
      title: 'Tu hésites entre 2 modèles ?',
      body: 'Note d’abord ton kilométrage annuel et ton temps libre. <strong>15 000 km/an et zéro temps</strong> = cardan obligatoire. <strong>5000 km/an et amateur de mécanique</strong> = la chaîne devient un rituel. <strong>Custom-attitude</strong> = la courroie te conviendra. Choisis la transmission AVANT le modèle.',
    }
  ],
};

export default transmissionMotoEnquete;
